import { TableHeaderCellsTypes } from "@/components/Table/table.types";
import { EditDetailsCTA } from "@/routes/AccountSettings/SenderDetails/EditDetailsCTA";
import type { SenderDetailsRow } from "@/routes/AccountSettings/SenderDetails/SenderDetails.types";
import { Alert, Box, Button, Theme, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useRevalidator, useRouteLoaderData } from "react-router-dom";
import TinyTable from "@/components/Table";
import { useEntri } from "@/components/Entri/useEntri";
import SenderDetailsFormDialog from "./SenderDetailsFormDialog";
import { prepareForEdit, isFreeDomain } from "./utils";

const headerCells: TableHeaderCellsTypes<SenderDetailsRow>[] = [
  { propName: "name", label: "Sender" },
  { propName: "dkimSigned", label: "DKIM status" },
  { propName: "dmarcSigned", label: "DMARC status" },
  { propName: "spfSigned", label: "SPF status" },
  { propName: "emailConfirmed", label: "Email Status" },
  { propName: "email", label: "Email address" },
  { propName: "address", label: "Address" },
  { propName: "city", label: "City" },
  { propName: "region", label: "State" },
  { propName: "country", label: "Country" },
  { propName: "cta", label: "", width: "4rem" },
];

function SenderDetails() {
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { validateDomain } = useEntri();
  const revalidator = useRevalidator();

  const { senderDetails: detailsData } = useRouteLoaderData("main") as {
    senderDetails: SenderDetailsRow;
  };

  const rowsData = useMemo(() => [detailsData], [detailsData]);

  const isFreeEmailDomain = useMemo(
    () => isFreeDomain(detailsData.email),
    [detailsData.email]
  );

  const hasAuthenticationIssues = useMemo(() => {
    if (isFreeEmailDomain) return false;
    return (
      !detailsData.dkimSigned ||
      !detailsData.dmarcSigned ||
      !detailsData.spfSigned
    );
  }, [
    detailsData.dkimSigned,
    detailsData.dmarcSigned,
    detailsData.spfSigned,
    isFreeEmailDomain,
  ]);

  const missingAuthMethods = useMemo(() => {
    if (isFreeEmailDomain) return [];
    const missing = [];
    if (!detailsData.dkimSigned) missing.push("DKIM");
    if (!detailsData.dmarcSigned) missing.push("DMARC");
    if (!detailsData.spfSigned) missing.push("SPF");
    return missing;
  }, [
    detailsData.dkimSigned,
    detailsData.dmarcSigned,
    detailsData.spfSigned,
    isFreeEmailDomain,
  ]);

  const formatAuthStatus = (isVerified: boolean) => {
    if (isFreeEmailDomain) {
      return (
        <Typography variant="label2R" color="grey.80" fontWeight="bold">
          N/A
        </Typography>
      );
    }
    return isVerified ? (
      <Typography variant="label2R" color="blue.125" fontWeight="bold">
        Verified
      </Typography>
    ) : (
      <Typography variant="label2R" color="red.100" fontWeight="bold">
        Not verified
      </Typography>
    );
  };

  const formattedRows = rowsData.map((row) => ({
    ...row,
    emailConfirmed: row.emailConfirmed ? (
      <Typography variant="label2R" color="blue.125" fontWeight="bold">
        Verified
      </Typography>
    ) : (
      <Typography variant="label2R" color="red.100" fontWeight="bold">
        Not verified
      </Typography>
    ),
    dkimSigned: formatAuthStatus(!!row?.dkimSigned),
    dmarcSigned: formatAuthStatus(!!row?.dmarcSigned),
    spfSigned: formatAuthStatus(!!row?.spfSigned),
  }));

  const handleValidateDomain = () => {
    validateDomain({
      email: rowsData[0].email,
      onSuccess: () => {
        console.log("DNS configuration completed successfully!");
        revalidator.revalidate();
      },
      onError: (error) => {
        console.error("DNS configuration failed:", error);
      },
    });
  };

  return (
    <Box mt={6}>
      {hasAuthenticationIssues && (
        <Alert
          variant="outlined"
          severity="info"
          sx={(theme: Theme) => ({
            mb: 5,
            borderColor: `${theme.palette.primary[60] as string} !important`,
            "& .MuiAlert-icon": {
              ml: 2,
            },
          })}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                aria-label="Validate Domain"
                size="small"
                onClick={handleValidateDomain}
                variant="contained"
                sx={{
                  mr: 1,
                  whiteSpace: "nowrap",
                  overflowWrap: "normal",
                  wordBreak: "normal",
                }}
              >
                Validate Domain
              </Button>
              <Button
                aria-label="Learn More"
                size="small"
                sx={{
                  mr: 4,
                  whiteSpace: "nowrap",
                  overflowWrap: "normal",
                  wordBreak: "normal",
                }}
                href="https://support.tinyalbert.ai/portal/en/kb/articles/new-email-rules-to-stop-spam"
                target="_blank"
                rel="noreferrer"
                id="campaign-calendar-close-info-alert-msg"
                variant="text"
              >
                Learn More
              </Button>
            </Box>
          }
        >
          <Typography variant="p3">
            <b>{missingAuthMethods.join("/")}</b> not signed. To comply with
            Gmail guidelines and improve deliverability, please set up the
            required DNS records.{" "}
            <b>
              If you've already configured them, note that it may take up to 48
              hours for changes to reflect.
            </b>
          </Typography>
        </Alert>
      )}
      <TinyTable
        headerCells={headerCells}
        rowsData={rowsData}
        rows={formattedRows}
        options={{
          currentSort: "name.asc",
          onSort: () => {},
          ActionCTA: EditDetailsCTA,
          showActionAlways: true,
          RowCTA: () => {
            setIsEditOpen(true);
          },
        }}
      />
      <SenderDetailsFormDialog
        open={isEditOpen}
        details={prepareForEdit(rowsData[0])}
        dialogCloseHandler={() => {
          setIsEditOpen(false);
        }}
        ctaId="myaccount-senderdetails-update"
      />
    </Box>
  );
}

export { SenderDetails };
